/* global dataLayer */
class Dropdownable extends HTMLElement {
  constructor () {
    super()
    this.init()
  }

  init () {
    // TODO Rethink dependency on classes (should not depend on tags instead?)
    this.mainClass = ''
  }

  connectedCallback () {
    if (this.mainClass && !this.classList.contains(this.mainClass)) {
      this.classList.add(this.mainClass)
    }
    this.classList.remove('rnk-Icon-spinner')
    this.setToggleEvent()
    this.setKeyboardEvent()
    this.announceState()
    this.adjustRightCorner()
  }

  disconnectedCallback () {
    document.body.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeydown)
  }

  get toggle () {
    return this.querySelector('dropdown-toggle')
  }

  get isOpen () {
    return this.classList.contains('open')
  }

  adjustRightCorner () {
    if (this.getAttribute('right-adjust') !== 'true') return

    const rect = this.getBoundingClientRect()
    const rightWindowGap = window.innerWidth - rect.right
    const dropdown = this.querySelector('dropdown-dropdown')
    if (rightWindowGap < dropdown.offsetWidth / 2) {
      dropdown.setAttribute('right', 'true')
    } else {
      dropdown.removeAttribute('right')
    }
  }

  toggleThis (target) {
    const isMe = (target === this)
    // `localName` y no `tagName`: `tagName` devuelve el nombre en MAYÚSCULAS y
    // hay motores de selectores que no lo casan con un elemento personalizado.
    // El navegador sí lo casa (los selectores de tipo son insensibles a caja en
    // HTML), así que esto no cambia nada en producción, pero sin ello el
    // componente es imposible de probar en jsdom.
    const isChild = (target.closest(this.localName) === this)
    // soon we will check if it is a nested dropdown
    if (isMe || isChild) {
      this.classList.toggle('open')
      this.classList.contains('open') && this.scrollIntoView({ behavior: 'smooth' })
      this.announceState()
      this.isOpen && this.trackOpening()
    } else {
      this.close()
    }
  }

  setToggleEvent () {
    this.onDocumentClick = (e) => this.toggleThis(e.target)
    document.body.addEventListener('click', this.onDocumentClick)
  }

  // Escape cierra el desplegable y devuelve el foco al disparador. Es criterio de
  // la definición de terminado de la ficha de acción ("los desplegables se cierran
  // con Escape"), pero no es propio de esa página: un panel que solo se puede
  // cerrar con el ratón deja atrapado a quien navega con teclado en CUALQUIER
  // desplegable del sitio.
  //
  // Va en `document` y no en el propio elemento porque el foco puede estar dentro
  // del panel, fuera de él o en ninguna parte, y en los dos últimos casos el
  // evento nunca llegaría hasta aquí por propagación.
  setKeyboardEvent () {
    this.onDocumentKeydown = (e) => {
      if (this.isActivationKey(e)) return this.activateFromKeyboard(e)
      // Abrir uno cierra los demás, igual que con el ratón: ahí el listener de
      // clic de `document.body` llega a TODAS las instancias y las que no son la
      // pulsada se cierran. Con el teclado cada instancia solo miraba si la tecla
      // era suya, así que dos paneles se quedaban abiertos a la vez.
      if (this.isActivationKeyElsewhere(e)) return this.close()
      if (e.key !== 'Escape' || !this.isOpen) return

      // La pregunta se hace ANTES de cerrar. Al cerrar, el panel pasa a
      // `visibility: hidden` y el navegador se lleva el foco al `<body>`: si se
      // preguntase después, `document.activeElement` ya no estaría dentro y el
      // foco no volvería nunca, que es justo el caso que esto arregla. jsdom no
      // calcula estilo, así que ahí la diferencia no se ve.
      const teniaElFoco = this.contains(document.activeElement)
      this.close()
      // Solo se recupera el foco si estaba dentro: si el usuario había seguido
      // navegando por la página, moverle el foco al disparador sería peor que no
      // hacer nada.
      if (teniaElFoco) this.toggle?.focus()
    }
    document.addEventListener('keydown', this.onDocumentKeydown)
  }

  // Enter y Espacio sobre el disparador. Un `<button>` los convertiría en clic
  // por su cuenta, pero `<dropdown-toggle>` es un elemento personalizado y no
  // hace nada: con solo `tabindex` se llegaba al disparador con el tabulador y
  // ahí se acababa el recorrido.
  isActivationKey (e) {
    return this.isActivationKeystroke(e) && e.target === this.toggle
  }

  // La pulsación que activa, sin mirar sobre quién cae.
  //
  // `e.repeat` fuera: mantener la tecla pulsada dispara un keydown por
  // repetición del teclado, y sobre un interruptor eso es abrir y cerrar el
  // panel en bucle mientras no se suelte.
  isActivationKeystroke (e) {
    return !e.repeat && (e.key === 'Enter' || e.key === ' ')
  }

  // La misma pulsación, pero sobre el disparador de OTRO desplegable.
  isActivationKeyElsewhere (e) {
    if (!this.isOpen || !this.isActivationKeystroke(e)) return false

    const otherToggle = e.target?.closest?.('dropdown-toggle')

    return Boolean(otherToggle) && otherToggle !== this.toggle
  }

  activateFromKeyboard (e) {
    // Sin esto, Espacio hace scroll de la página bajo el panel que se acaba de
    // abrir, y Enter puede enviar el formulario que envuelva al desplegable.
    e.preventDefault()
    this.toggleThis(this.toggle)
  }

  // El estado abierto vivía solo en una clase CSS, así que un lector de pantalla
  // anunciaba el disparador igual abierto que cerrado.
  //
  // `tabindex` también se pone aquí: `<dropdown-toggle>` es un elemento
  // personalizado, no un `<button>`, así que por defecto NO es alcanzable con el
  // tabulador. Sin él, devolverle el foco al cerrar con Escape no hace nada, y
  // además nunca se podía llegar al desplegable con el teclado. Solo se pone
  // cuando el marcado no trae uno propio.
  announceState () {
    const toggle = this.toggle
    if (!toggle) return

    toggle.setAttribute('aria-expanded', String(this.isOpen))
    toggle.setAttribute('aria-haspopup', 'true')
    if (!toggle.hasAttribute('tabindex')) toggle.setAttribute('tabindex', '0')
    // `role="button"` va con el `tabindex`: sin él, un lector de pantalla anuncia
    // un elemento desconocido que resulta ser enfocable, y no dice que se pueda
    // pulsar. Es lo que el disparador es.
    if (!toggle.hasAttribute('role')) toggle.setAttribute('role', 'button')
  }

  // Avisa a analítica de que el desplegable se ha abierto, para poder comparar
  // aperturas con clics en lo que hay dentro (Trello #1986: cuánta gente abre
  // «Comprar» y no elige bróker). Solo lo hacen los desplegables que la vista
  // marca con `data-link-id`: el elemento se usa en todo el sitio y la mayoría
  // de sus aperturas no le interesan a nadie.
  //
  // Guard sobre `dataLayer`: si GTM no se carga (local, bloqueadores), abrir el
  // desplegable no puede romperse por no poder medirlo.
  trackOpening () {
    const linkId = this.dataset.linkId
    if (!linkId || typeof dataLayer === 'undefined') return

    dataLayer.push({ event: 'dropdownOpen', linkId })
  }

  close () {
    // El listener de clic vive en `document.body`, así que CADA clic de la página
    // llega a CADA desplegable y acababa aquí. Sin esta salida temprana, cerrar
    // lo ya cerrado repintaba cuatro atributos por desplegable y por clic.
    if (!this.isOpen) return

    this.classList.remove('open')
    this.announceState()
  }
}

export default Dropdownable
